/**
 * Reactions in a live: the seven a viewer can send, how many one person may,
 * and the shapes the room, the API and the pages share. Designed in
 * `prototype/live-sessions.html` (screens 1 to 8, approved 19 Sep 2026).
 *
 * One clock for the live and the replay: a reaction's `at` is the second of
 * the room's recording, counted from its first frame, so second 1,788 of the
 * live is second 1,788 of the replay.
 */

import type { ReactionCounts, ReactionKind } from "./live-protocol";

export { isReactionKind, REACTION_KINDS, type ReactionCounts, type ReactionKind } from "./live-protocol";

/** One reaction as a viewer sees it. */
export interface Reaction {
  kind: ReactionKind;
  emoji: string;
  label: string;
  /** How the studio's line about the room says it: "Mostly finding it useful". */
  mood: string;
}

/** The seven, in the order they show. */
export const REACTIONS: readonly Reaction[] = [
  { kind: "love", emoji: "❤️", label: "Love it", mood: "loving it" },
  { kind: "agree", emoji: "👍", label: "Agree", mood: "agreeing" },
  { kind: "clap", emoji: "👏", label: "Great answer", mood: "clapping" },
  { kind: "idea", emoji: "💡", label: "Useful", mood: "finding it useful" },
  { kind: "funny", emoji: "😂", label: "Funny", mood: "laughing" },
  { kind: "party", emoji: "🎉", label: "Celebrate", mood: "celebrating" },
  { kind: "same", emoji: "🙋", label: "Me too", mood: "saying me too" },
];

// ── how many one person may send ────────────────────────────────────────
/** Taps in a quick run before the limit starts. */
export const REACT_BURST = 10;
/** Then one tap back every this long, while they keep tapping or after they stop. */
export const REACT_REFILL_MS = 2000;

/** One person's allowance: taps left, and when it last refilled. */
export interface ReactBucket {
  left: number;
  at: number;
}

export function freshBucket(nowMs: number): ReactBucket {
  return { left: REACT_BURST, at: nowMs };
}

/**
 * Spend one tap if the person has one. Past the limit the tap is not sent;
 * the page still floats it on their own screen, and nobody is warned.
 */
export function takeTap(bucket: ReactBucket, nowMs: number): boolean {
  const refilled = Math.floor((nowMs - bucket.at) / REACT_REFILL_MS);
  if (refilled > 0) {
    bucket.left = Math.min(REACT_BURST, bucket.left + refilled);
    bucket.at += refilled * REACT_REFILL_MS;
  }
  if (bucket.left <= 0) return false;
  bucket.left -= 1;
  return true;
}

// ── the room ────────────────────────────────────────────────────────────
/** How often the room sends every chat socket the totals of the second just gone. */
export const REACT_TICK_MS = 1000;
/** How often the room writes its finished seconds to D1. */
export const REACT_SAVE_MS = 5000;

// ── the replay ──────────────────────────────────────────────────────────
/** How often a replay page sends its taps to the API. */
export const REPLAY_SEND_MS = 5000;
/** The most taps one replay batch may carry. */
export const REPLAY_BATCH_MAX = 60;

/** `POST /api/watch/:token/reactions`: taps made while watching the replay. */
export interface ReplayReactionsRequest {
  webinar_id: string;
  /** The series' session whose replay they were on; the webinar's latest replay when absent. */
  session_id?: string;
  taps: Array<{ kind: ReactionKind; at: number }>;
}

// ── the timeline, for the replay's bar and the host's page ──────────────
/** Seconds per bar of the strip. */
export const REACTION_BIN_SEC = 10;

/** One of the busiest moments, counted by people, not taps. */
export interface ReactionMoment {
  /** The second it peaked. */
  at: number;
  live: ReactionCounts;
  replay: ReactionCounts;
  /** How many different people reacted, in the live and on the replay. */
  people: { live: number; replay: number };
  /** The words spoken around that second, once the replay's transcript exists. */
  words: string | null;
  /** The chapter it falls in, once there are chapters. */
  chapter: string | null;
  /** Who reacted, for the host only; never sent to a viewer. */
  who?: Array<{ registrantId: string; name: string }>;
}

/** Where people reacted, second by second in bins, and the busiest moments. */
export interface ReactionTimeline {
  /** Seconds per bin (REACTION_BIN_SEC). */
  bin: number;
  /** The recording's length in seconds. */
  lengthSec: number;
  /** Taps per bin, in the live and on the replay. */
  live: number[];
  replay: number[];
  /** The busiest moments, the busiest first (at most 5). */
  moments: ReactionMoment[];
}

/** One person, as the host sees who reacted most. */
export interface Reactor {
  registrantId: string;
  name: string;
  taps: number;
  /** Their share of every tap, 0 to 1. */
  share: number;
  /** In how many of the busiest moments they reacted. */
  moments: number;
  /** The kind they sent most. */
  top: ReactionKind;
  /** Their private thread with the host, for a reply. */
  threadId: string | null;
}

/** A stretch of the live the host compares: a chapter, or ten minutes until there are chapters. */
export interface ReactionGroup {
  name: string;
  from: number;
  to: number;
  taps: number;
  perMinute: number;
}

/** `GET /api/webinars/:id/live/reactions`: what the host's "What they loved" draws. */
export interface LiveReactionsResponse {
  timeline: ReactionTimeline;
  totals: { live: number; replay: number; peopleLive: number; peopleReplay: number; cameLive: number };
  /** Chapters when the replay has them, else ten-minute stretches. */
  groups: ReactionGroup[];
  byChapter: boolean;
  /** Everyone who reacted, the most first. */
  reactors: Reactor[];
}
